import React, {useEffect, useRef, useState} from 'react';
import {StyleSheet, View, ScrollView, Text, TextInput, TouchableOpacity as RNTouchableOpacity, Modal} from 'react-native';
import {TouchableOpacity} from 'react-native-gesture-handler';
import ChessboardWithArrow from '@/Components/ChessboardWithArrow';
import {resolvePuzzleFen} from '../../utils/puzzle/resolvePuzzleFen';
import {getPuzzlePollWindowEnd} from '../../utils/puzzle/getPuzzlePollWindowEnd';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {FontSizes, vs} from '@/Utils/Dimensions';
import Loader from '@/Atoms/CCLoader/CCLoader';
import {AwardIcon} from '../../recognition/AwardIcon';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import {
  useGetClassroomAwardFeed,
  useGetClassroomLiveAttempts,
  useGetStudentSessionStats,
  useGetCoachLiveAlerts,
  useGetClassBrief,
  useGetStudentLongitudinal,
  useSubmitZoneFeedback,
  LiveAttemptRow,
  AwardFeedEvent,
  CoachAlert,
} from '../../recognition/api/useRecognitionApi';

interface SimpleStudent {
  user_id: number;
  name: string;
  manual_award_counts?: Record<string, number>;
}

interface Props {
  meetingRole: string;
  meetingId: string;
  occurenceId: string;
  source: string;
  currentActivePuzzle: any;
  activeTabRight: number;
  onAward?: (studentId: string, awardType: string) => void;
  classStudents?: SimpleStudent[];
  // {String(user_id): {zone, reasons, action}} — coach-only
  zoneByUserId?: Record<string, {zone: string; reasons: string[]; action: string}>;
  currentUserId?: string | number;
}

// Coach-only zone highlight (red/yellow flagged; green = no highlight).
const ZONE_DOT_COLOR: Record<string, string> = {
  red: '#DC2626',
  yellow: '#D97706',
  green: '#16A34A',
};
const ZONE_TINT: Record<string, string> = {
  red: '#FDECEC',
  yellow: '#FEF3E2',
  green: '#EAF7EF',
};

const MANUAL_AWARDS = [
  {key: 'good_try', label: 'Good Try', icon: '🌱', color: '#3DAB9E'},
  {key: 'great_effort', label: 'Great Effort', icon: '💪', color: '#E17B21'},
  {key: 'good_thinking', label: 'Good Thinking', icon: '💡', color: '#FACF47'},
  {key: 'focused', label: 'Focused', icon: '🎯', color: '#937ADB'},
  {key: 'team_player', label: 'Team Player', icon: '🤝', color: '#4D3F37'},
];

const EVENT_META: Record<string, {label: string; color: string}> = {
  good_try: {label: 'Good Try', color: '#3DAB9E'},
  great_effort: {label: 'Great Effort', color: '#E17B21'},
  good_thinking: {label: 'Good Thinking', color: '#FACF47'},
  focused: {label: 'Focused', color: '#937ADB'},
  team_player: {label: 'Team Player', color: '#4D3F37'},
  puzzle_attempt: {label: 'Attempt', color: '#9AA0A6'},
  correct_solve: {label: 'Correct', color: '#22A06B'},
  fastest_finger: {label: 'Speed Flash', color: '#FF7A1A'},
  joined_on_time: {label: 'On Time', color: '#4F8DF7'},
  homework_complete: {label: 'Homework', color: '#5B6BFA'},
};

const ClassroomActivityPanel: React.FC<Props> = ({
  meetingRole,
  meetingId,
  occurenceId,
  source,
  currentActivePuzzle,
  onAward,
  classStudents = [],
  zoneByUserId = {},
  currentUserId,
}) => {
  const isCoach = meetingRole !== 'student';
  const loggedInUserId = useLoggedInUserStore(state => state.userId);
  const effectiveUserId = currentUserId ?? loggedInUserId;
  const [tab, setTab] = useState<'live' | 'awards'>('live');
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
  const [expandedStatsUserId, setExpandedStatsUserId] = useState<number | null>(null);
  const [pendingAward, setPendingAward] = useState<{userId: number; awardKey: string} | null>(null);
  const [puzzleModalRow, setPuzzleModalRow] = useState<LiveAttemptRow | null>(null);
  const [briefModalOpen, setBriefModalOpen] = useState(false);
  const [historyModalUserId, setHistoryModalUserId] = useState<number | null>(null);
  const [homeworkExpanded, setHomeworkExpanded] = useState(false);
  const [trickiestOpen, setTrickiestOpen] = useState(false);

  // Coach feedback on the zone assessment for the student open in the detail modal.
  const [fbReasonOpen, setFbReasonOpen] = useState(false);
  const [fbReason, setFbReason] = useState('');
  const [fbSubmitted, setFbSubmitted] = useState<null | 'correct' | 'incorrect'>(null);
  const {mutate: submitZoneFeedback} = useSubmitZoneFeedback();
  useEffect(() => {
    setFbReasonOpen(false);
    setFbReason('');
    setFbSubmitted(null);
  }, [puzzleModalRow?.user_id]);
  const sendZoneFeedback = (verdict: 'correct' | 'incorrect') => {
    const zi = puzzleModalRow ? zoneByUserId[String(puzzleModalRow.user_id)] : undefined;
    if (!puzzleModalRow || !zi) return;
    submitZoneFeedback(
      {
        meetingId,
        occurenceId,
        meetingRole,
        studentUserId: puzzleModalRow.user_id,
        zone: zi.zone,
        reasons: zi.reasons,
        action: zi.action,
        verdict,
        reason: verdict === 'incorrect' ? fbReason.trim() : undefined,
      },
      {onSuccess: () => setFbSubmitted(verdict)},
    );
  };

  const puzzleId = currentActivePuzzle?.puzzleId ? Number(currentActivePuzzle.puzzleId) : null;
  // The puzzle board = starting_fen with the coach's recorded line replayed.
  // Raw starting_fen is usually the normal start, so showing it looks "not loaded".
  const activePuzzleFen = resolvePuzzleFen(
    currentActivePuzzle?.startingFEN,
    currentActivePuzzle?.moveList,
  );

  // CO-3: live attempts only need fast polling while students can still be
  // moving on this puzzle (timer + 4s client expiry + 10s grace). Once that
  // window passes, the puzzle is done and the slow interval is enough.
  const tabRef = useRef(tab);
  tabRef.current = tab;
  const [puzzleRunning, setPuzzleRunning] = useState<boolean>(() => {
    if (puzzleId === null) return false;
    const endsAt = getPuzzlePollWindowEnd({
      source,
      puzzleTime: currentActivePuzzle?.puzzleTime,
      remainingTime: currentActivePuzzle?.remainingTime,
      meetingId,
      occurenceId,
      puzzleId,
    });
    return Date.now() < endsAt;
  });

  const {data: liveData, isLoading: liveLoading, refetch: refetchLive} = useGetClassroomLiveAttempts(
    meetingId,
    occurenceId,
    puzzleId,
    {enabled: tab === 'live' && !!meetingId && !!occurenceId, refetchInterval: puzzleRunning ? (isCoach ? 3000 : 6000) : 30000, meetingRole},
  );

  useEffect(() => {
    if (puzzleId === null) {
      setPuzzleRunning(false);
      return;
    }
    const endsAt = getPuzzlePollWindowEnd({
      source,
      puzzleTime: currentActivePuzzle?.puzzleTime,
      remainingTime: currentActivePuzzle?.remainingTime,
      meetingId,
      occurenceId,
      puzzleId,
    });
    const msLeft = endsAt - Date.now();
    if (msLeft <= 0) {
      setPuzzleRunning(false);
      return;
    }
    setPuzzleRunning(true);
    const windowTimeout = setTimeout(() => {
      setPuzzleRunning(false);
      if (tabRef.current === 'live') {
        refetchLive();
      }
    }, msLeft);
    return () => clearTimeout(windowTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    meetingId,
    occurenceId,
    puzzleId,
    source,
    currentActivePuzzle?.puzzleTime,
    currentActivePuzzle?.remainingTime,
  ]);
  const {data: feedData, isLoading: feedLoading} = useGetClassroomAwardFeed(
    meetingId,
    occurenceId,
    {enabled: tab === 'awards' && !!meetingId && !!occurenceId, refetchInterval: 5000},
  );
  const {data: statsData} = useGetStudentSessionStats(
    meetingId,
    occurenceId,
    expandedStatsUserId,
    {enabled: !!expandedStatsUserId && tab === 'live'},
  );
  const {data: alertsData} = useGetCoachLiveAlerts(
    meetingId, occurenceId, puzzleId,
    {enabled: isCoach && tab === 'live' && !!meetingId && !!occurenceId, refetchInterval: 5000, meetingRole},
  );
  const {data: briefData, refetch: refetchBrief, isFetching: briefLoading} = useGetClassBrief(
    meetingId, occurenceId,
    {enabled: briefModalOpen && isCoach, meetingRole},
  );
  const {data: longitudinalData, refetch: refetchLong, isFetching: longLoading} = useGetStudentLongitudinal(
    meetingId, occurenceId, historyModalUserId, 5,
    {enabled: !!historyModalUserId && isCoach, meetingRole},
  );

  const liveRows: LiveAttemptRow[] = liveData?.attempts ?? [];
  const coachAlerts: CoachAlert[] = (alertsData?.alerts ?? []);
  const stuckUserIds = new Set(
    coachAlerts.filter(a => a.type === 'stuck').map(a => (a as any).user_id as number),
  );
  const feedRows: AwardFeedEvent[] = feedData?.events ?? [];

  const fallbackStudents: LiveAttemptRow[] = classStudents.map(s => ({
    user_id: s.user_id,
    name: s.name,
    status: 'not_attempted',
    solve_time: null,
    attempts: 0,
  }));
  const liveDisplay = liveRows.length > 0 ? liveRows : fallbackStudents;
  const awardCountsByUser: Record<number, Record<string, number>> = {};
  for (const s of classStudents) {
    if (s.manual_award_counts) awardCountsByUser[s.user_id] = s.manual_award_counts;
  }

  const renderTabBtn = (key: 'live' | 'awards', label: string) => (
    <RNTouchableOpacity
      onPress={() => setTab(key)}
      activeOpacity={0.7}
      style={[styles.tabBtn, tab === key && styles.tabBtnActive]}>
      <CCText
        fontSize={FontSizes[14]}
        style={[styles.tabBtnText, tab === key && styles.tabBtnTextActive]}>
        {label}
      </CCText>
    </RNTouchableOpacity>
  );

  const statusBadge = (row: LiveAttemptRow) => {
    if (row.status === 'correct') {
      return (
        <View style={[styles.badge, {backgroundColor: '#E5F6EE'}]}>
          <Text style={{color: '#22A06B', fontWeight: '700'}}>
            ✓ {row.solve_time ? `${row.solve_time}s` : ''}
          </Text>
        </View>
      );
    }
    if (row.status === 'wrong') {
      return (
        <View style={[styles.badge, {backgroundColor: '#FDECEC'}]}>
          <Text style={{color: '#D14343', fontWeight: '700'}}>✗ {row.attempts}x</Text>
        </View>
      );
    }
    if (row.status === 'in_progress') {
      return (
        <View style={[styles.badge, {backgroundColor: '#FFF6E0'}]}>
          <Text style={{color: '#B8780C', fontWeight: '700'}}>⏳</Text>
        </View>
      );
    }
    return (
      <View style={[styles.badge, {backgroundColor: '#F1F2F4'}]}>
        <Text style={{color: '#6E7681'}}>—</Text>
      </View>
    );
  };

  const moveDetail = (row: LiveAttemptRow) => {
    const hasMoves = (row.wrong_moves?.length ?? 0) > 0 || row.correct_move;
    const hasExtra = (row.stars_earned ?? 0) > 0 || (row.tournament_wins ?? 0) > 0 || (row.session_stars ?? 0) > 0;
    if (!hasMoves && !hasExtra) return null;
    return (
      <View style={styles.moveDetailRow}>
        {row.correct_move && (
          <Text style={styles.moveChipCorrect}>▶ {row.correct_move}</Text>
        )}
        {(row.wrong_moves ?? []).map((m, i) => (
          <Text key={i} style={styles.moveChipWrong}>✗ {m}</Text>
        ))}
        {(row.stars_earned ?? 0) > 0 && (
          <Text style={styles.moveChipStar}>+{row.stars_earned}★ puzzle</Text>
        )}
        {(row.tournament_wins ?? 0) > 0 && (
          <Text style={styles.moveChipTourney}>🏆 {row.tournament_wins}W</Text>
        )}
        {(row.session_stars ?? 0) > 0 && (
          <Text style={styles.moveChipSession}>★{row.session_stars} total</Text>
        )}
      </View>
    );
  };

  // Sort live rows: correct first, then in_progress, then wrong, then not_attempted
  const STATUS_RANK: Record<string, number> = {correct: 0, in_progress: 1, wrong: 2, not_attempted: 3};
  const sortedLiveDisplay = [...liveDisplay].sort(
    (a, b) => (STATUS_RANK[a.status] ?? 3) - (STATUS_RANK[b.status] ?? 3),
  );

  return (
    <View style={styles.container}>
      <View style={styles.tabBar}>
        {renderTabBtn('live', 'Live')}
        {renderTabBtn('awards', 'Awards')}
      </View>

      {tab === 'live' && (
        <View style={{flex: 1}}>
          <View style={styles.subTitleRow}>
            <CCText fontSize={FontSizes[13]} style={styles.subTitle}>
              {puzzleId ? `Puzzle ${puzzleId}` : 'No active puzzle'}
            </CCText>
            {isCoach && (
              <RNTouchableOpacity onPress={() => setBriefModalOpen(true)} style={styles.briefBtn}>
                <Text style={{color: CCColors.Primary.Brown, fontWeight: '700', fontSize: 11}}>📊 Brief</Text>
              </RNTouchableOpacity>
            )}
          </View>

          {/* Tier 1: live alert ticker */}
          {isCoach && coachAlerts.length > 0 && (
            <View style={styles.alertTicker}>
              {coachAlerts.slice(0, 4).map((a, i) => {
                let label = ''; let color = '#6E7681';
                if (a.type === 'idle') { label = `💤 ${(a as any).name} idle ${(a as any).idle_seconds}s`; color = '#B8780C'; }
                else if (a.type === 'stuck') { label = `⚠ ${(a as any).name} stuck (${(a as any).attempts}x)`; color = '#D14343'; }
                else if (a.type === 'speed_outlier') { label = `⚡ ${(a as any).name} solved ${(a as any).solve_time}s`; color = '#22A06B'; }
                else if (a.type === 'common_wrong_move') { label = `🚩 ${(a as any).count}× wrong: ${(a as any).move}`; color = '#5B6BFA'; }
                else if (a.type === 'disengaged') { label = `🛑 ${(a as any).name} not started`; color = '#9AA0A6'; }
                return (
                  <Text key={i} style={[styles.alertChip, {color, borderColor: color + '55', backgroundColor: color + '15'}]}>
                    {label}
                  </Text>
                );
              })}
            </View>
          )}
          {puzzleId !== null && liveLoading && liveRows.length === 0 ? (
            <Loader loading={true} bgWhite={true} />
          ) : sortedLiveDisplay.length === 0 ? (
            <View style={styles.emptyState}>
              <CCText fontSize={FontSizes[14]} style={styles.emptyDescription}>
                Waiting for students to join...
              </CCText>
            </View>
          ) : (
            <ScrollView style={styles.scrollView}>
              {/* Table header */}
              <View style={styles.tableHeader}>
                <Text style={[styles.colHeader, styles.colRank]}>#</Text>
                <Text style={[styles.colHeader, styles.colName]}>Student</Text>
                <Text style={[styles.colHeader, styles.colStatus]}>Status</Text>
                <Text style={[styles.colHeader, styles.colStars]}>★</Text>
                {isCoach && onAward && <Text style={[styles.colHeader, styles.colAction]} />}
              </View>

              {sortedLiveDisplay.map((row, idx) => {
                const isExpanded = expandedUserId === row.user_id;
                const isDetailExpanded = expandedStatsUserId === row.user_id;
                const badgeCounts = awardCountsByUser[row.user_id] ?? {};
                const givenBadges = MANUAL_AWARDS.filter(a => (badgeCounts[a.key] ?? 0) > 0);
                const studentFromPanel = classStudents.find(s => s.user_id === row.user_id);
                const totalStars = row.session_stars ?? (studentFromPanel as any)?.stars ?? null;
                const hasMoveDetail = !!(row.correct_move || (row.wrong_moves?.length ?? 0) > 0);
                const zinfo = isCoach ? zoneByUserId[String(row.user_id)] : undefined;
                const zzone = zinfo && zinfo.zone && zinfo.zone !== 'green' ? zinfo.zone : undefined;

                const isOwnRow =
                  effectiveUserId != null &&
                  effectiveUserId !== '' &&
                  String(effectiveUserId) === String(row.user_id);
                const canViewDetails = isCoach || (isOwnRow && hasMoveDetail);

                return (
                  <View key={row.user_id}>
                    {/* Main table row — coach tap opens puzzle detail modal, student tap expands own row attempts */}
                    <TouchableOpacity
                      activeOpacity={canViewDetails ? 0.7 : 1}
                      onPress={() => {
                        if (isCoach) { setPuzzleModalRow(row); return; }
                        if (isOwnRow && hasMoveDetail) setExpandedStatsUserId(isDetailExpanded ? null : row.user_id);
                      }}
                      style={[
                        styles.tableRow,
                        idx % 2 === 0 && styles.tableRowAlt,
                        row.status === 'correct' && styles.tableRowCorrect,
                        zzone && {borderLeftWidth: 5, borderLeftColor: ZONE_DOT_COLOR[zzone],
                                  backgroundColor: ZONE_TINT[zzone]},
                      ]}>
                      {/* Rank */}
                      <Text style={[styles.colRank, styles.rankText]}>{idx + 1}</Text>

                      {/* Name */}
                      <View style={[styles.colName, {flexDirection: 'row', alignItems: 'center', gap: 4}]}>
                        {zzone && (
                          <Text style={{color: ZONE_DOT_COLOR[zzone] || '#999',
                                        fontSize: 18, lineHeight: 18}}>●</Text>
                        )}
                        {isCoach && stuckUserIds.has(row.user_id) && (
                          <Text style={styles.stuckDot}>●</Text>
                        )}
                        <CCText fontSize={FontSizes[13]} style={styles.liveName} numberOfLines={1}>
                          {row.name}
                        </CCText>
                        {isCoach && <Text style={styles.expandCaret}>↗</Text>}
                        {!isCoach && isOwnRow && hasMoveDetail && (
                          <Text style={styles.expandCaret}>
                            {isDetailExpanded ? '▾' : '▸'}
                          </Text>
                        )}
                      </View>

                      {/* Status + time */}
                      <View style={[styles.colStatus, {alignItems: 'flex-end'}]}>
                        {statusBadge(row)}
                      </View>

                      {/* Stars */}
                      <View style={[styles.colStars, {alignItems: 'center'}]}>
                        {totalStars != null && totalStars > 0
                          ? <Text style={styles.starBadge}>★{totalStars}</Text>
                          : <Text style={{color: '#C4C7CC', fontSize: 12}}>—</Text>
                        }
                      </View>

                      {/* Award button */}
                      {isCoach && onAward && (
                        <View style={styles.colAction}>
                          <TouchableOpacity
                            onPress={() => {
                              setExpandedUserId(isExpanded ? null : row.user_id);
                              setPendingAward(null);
                            }}
                            style={styles.awardOpenBtn}>
                            <Text style={{color: '#fff', fontWeight: '700', fontSize: 11}}>
                              {isExpanded ? '▾' : '+ Award'}
                            </Text>
                          </TouchableOpacity>
                        </View>
                      )}
                    </TouchableOpacity>

                    {/* Always-visible badge chips */}
                    {givenBadges.length > 0 && (
                      <View style={styles.badgeSubRow}>
                        {givenBadges.map(a => (
                          <Text key={a.key} style={[styles.inlineBadgeChip, {color: a.color, borderColor: a.color + '55', backgroundColor: a.color + '15'}]}>
                            {a.icon} {a.label} ×{badgeCounts[a.key]}
                          </Text>
                        ))}
                      </View>
                    )}

                    {/* Expandable detail row — move data only */}
                    {isDetailExpanded && (
                      <View style={styles.detailRow}>
                        {/* Move chips */}
                        {(row.correct_move || (row.wrong_moves?.length ?? 0) > 0 || (row.stars_earned ?? 0) > 0 || (row.tournament_wins ?? 0) > 0) && (
                          <View style={styles.detailSection}>
                            <Text style={styles.detailSectionLabel}>Puzzle moves</Text>
                            <View style={styles.chipRow}>
                              {row.correct_move && <Text style={styles.moveChipCorrect}>▶ {row.correct_move}</Text>}
                              {(row.wrong_moves ?? []).map((m, i) => <Text key={i} style={styles.moveChipWrong}>✗ {m}</Text>)}
                              {(row.stars_earned ?? 0) > 0 && <Text style={styles.moveChipStar}>+{row.stars_earned}★ puzzle</Text>}
                              {(row.tournament_wins ?? 0) > 0 && <Text style={styles.moveChipTourney}>🏆 {row.tournament_wins}W</Text>}
                              {(row.session_stars ?? 0) > 0 && <Text style={styles.moveChipSession}>★{row.session_stars} total</Text>}
                            </View>
                          </View>
                        )}
                        {statsData && expandedStatsUserId === row.user_id && (
                          <View style={styles.detailSection}>
                            <Text style={styles.detailSectionLabel}>Session</Text>
                            <View style={styles.chipRow}>
                              <Text style={styles.moveChipSession}>{statsData.puzzles_solved}/{statsData.total_puzzles} puzzles</Text>
                              {statsData.homework_completed && <Text style={styles.moveChipCorrect}>✓ HW</Text>}
                            </View>
                          </View>
                        )}
                      </View>
                    )}

                    {/* Award picker */}
                    {isExpanded && isCoach && onAward && (
                      <View style={styles.pickerContainer}>
                        {pendingAward && pendingAward.userId === row.user_id ? (
                          <View style={styles.confirmBox}>
                            <CCText fontSize={FontSizes[13]} style={styles.confirmText}>
                              Give {MANUAL_AWARDS.find(a => a.key === pendingAward.awardKey)?.label}{' '}
                              to {row.name.split(' ')[0]}?
                            </CCText>
                            <View style={styles.confirmBtns}>
                              <TouchableOpacity onPress={() => setPendingAward(null)} style={styles.btnNo}>
                                <Text>No</Text>
                              </TouchableOpacity>
                              <TouchableOpacity
                                onPress={() => {
                                  onAward(String(row.user_id), pendingAward.awardKey);
                                  setPendingAward(null);
                                  setExpandedUserId(null);
                                }}
                                style={styles.btnYes}>
                                <Text style={{color: '#fff', fontWeight: '800'}}>Yes, send</Text>
                              </TouchableOpacity>
                            </View>
                          </View>
                        ) : (
                          <View style={styles.awardGrid}>
                            {MANUAL_AWARDS.map(a => {
                              const cnt = awardCountsByUser[row.user_id]?.[a.key] ?? 0;
                              return (
                                <TouchableOpacity
                                  key={a.key}
                                  onPress={() => setPendingAward({userId: row.user_id, awardKey: a.key})}
                                  style={[styles.awardChip, {backgroundColor: a.color}]}>
                                  <Text style={styles.awardChipIcon}>{a.icon}</Text>
                                  <Text style={styles.awardChipText}>{a.label}{cnt > 0 ? ` ×${cnt}` : ''}</Text>
                                </TouchableOpacity>
                              );
                            })}
                          </View>
                        )}
                      </View>
                    )}
                  </View>
                );
              })}
            </ScrollView>
          )}
        </View>
      )}

      {tab === 'awards' && (
        <View style={{flex: 1}}>
          {feedLoading && feedRows.length === 0 ? (
            <Loader loading={true} bgWhite={true} />
          ) : feedRows.length === 0 ? (
            <View style={styles.emptyState}>
              <CCText fontSize={FontSizes[14]} style={styles.emptyDescription}>
                No awards yet. Stars and badges show up here.
              </CCText>
            </View>
          ) : (
            <ScrollView style={styles.scrollView}>
              {feedRows.map(ev => {
                const meta = EVENT_META[ev.event_type] || {label: ev.event_type, color: '#5B6BFA'};
                const label = ev.label_override || meta.label;
                return (
                  <View key={ev.id} style={styles.feedRow}>
                    <View style={styles.feedIcon}>
                      <AwardIcon awardKey={ev.event_type} iconUrl={ev.icon_url} size={28} color={meta.color} />
                    </View>
                    <View style={{flex: 1}}>
                      <CCText fontSize={FontSizes[13]} style={styles.feedName}>
                        {ev.name}
                      </CCText>
                      <CCText fontSize={FontSizes[12]} style={styles.feedLabel}>
                        {label}
                        {ev.stars_awarded > 1 ? ` · +${ev.stars_awarded}★` : ' · +1★'}
                      </CCText>
                    </View>
                    <CCText fontSize={FontSizes[11]} style={styles.feedTime}>
                      {ev.created_at}
                    </CCText>
                  </View>
                );
              })}
            </ScrollView>
          )}
        </View>
      )}

      {/* Puzzle detail modal — coach taps a student row */}
      <Modal
        visible={!!puzzleModalRow}
        transparent
        animationType="fade"
        onRequestClose={() => setPuzzleModalRow(null)}>
        <View style={styles.modalBackdrop}>
          <View style={styles.modalCard}>
            {/* Header */}
            <View style={styles.modalHeader}>
              <View style={{flex: 1}}>
                <CCText fontSize={FontSizes[14]} style={{fontWeight: '700'}}>
                  {puzzleModalRow?.name}
                </CCText>
                <CCText fontSize={FontSizes[12]} style={{color: '#6E7681'}}>
                  Puzzle {puzzleId}
                </CCText>
              </View>
              <RNTouchableOpacity onPress={() => setPuzzleModalRow(null)} style={styles.modalCloseBtn}>
                <Text style={{fontSize: 18, color: '#6E7681'}}>✕</Text>
              </RNTouchableOpacity>
            </View>

            {/* Body scrolls so the board + moves never crop on small screens;
                header (above) + action row (below) stay pinned. */}
            <ScrollView style={{flexShrink: 1}} contentContainerStyle={{paddingBottom: vs(6)}}>

            {/* Zone — why this student is flagged + what to do */}
            {(() => {
              const zi = puzzleModalRow ? zoneByUserId[String(puzzleModalRow.user_id)] : undefined;
              if (!zi) return null;
              const zc = ZONE_DOT_COLOR[zi.zone] || '#16A34A';
              const title = zi.zone === 'red' ? '🔴 Needs attention'
                : zi.zone === 'yellow' ? '🟡 Keep an eye' : '🟢 On track';
              return (
                <View style={{borderLeftWidth: 4, borderLeftColor: zc,
                              backgroundColor: ZONE_TINT[zi.zone] || '#EAF7EF',
                              borderRadius: 8, padding: 10, marginBottom: 10, gap: 6}}>
                  <CCText fontSize={FontSizes[13]} style={{fontWeight: '700', color: zc}}>{title}</CCText>
                  {(zi.reasons ?? []).length > 0 && (
                    <CCText fontSize={FontSizes[12]} style={{color: '#4D3F37'}}>
                      {(zi.reasons ?? []).join(' · ')}
                    </CCText>
                  )}
                  {!!zi.action && (
                    <CCText fontSize={FontSizes[12]} style={{color: '#4D3F37'}}>
                      <CCText fontSize={FontSizes[12]} style={{fontWeight: '700'}}>Do: </CCText>
                      {zi.action}
                    </CCText>
                  )}
                  {/* Coach feedback: is this assessment right? */}
                  {fbSubmitted ? (
                    <CCText fontSize={FontSizes[12]} style={{color: '#6E7681', fontStyle: 'italic'}}>
                      Thanks — marked {fbSubmitted}.
                    </CCText>
                  ) : fbReasonOpen ? (
                    <View style={{gap: 6}}>
                      <TextInput
                        value={fbReason}
                        onChangeText={setFbReason}
                        placeholder="What's off about this assessment?"
                        placeholderTextColor="#9AA0A6"
                        multiline
                        style={{borderWidth: 1, borderColor: 'rgba(38,35,34,0.2)', borderRadius: 8,
                                padding: 8, minHeight: 48, fontSize: 13, color: '#26231F', backgroundColor: '#fff'}}
                      />
                      <View style={{flexDirection: 'row', gap: 8}}>
                        <RNTouchableOpacity onPress={() => sendZoneFeedback('incorrect')} style={styles.fbBtnPrimary}>
                          <Text style={{color: '#fff', fontWeight: '700', fontSize: 12}}>Submit</Text>
                        </RNTouchableOpacity>
                        <RNTouchableOpacity onPress={() => {setFbReasonOpen(false); setFbReason('');}} style={styles.fbBtn}>
                          <Text style={{color: '#4D3F37', fontWeight: '700', fontSize: 12}}>Cancel</Text>
                        </RNTouchableOpacity>
                      </View>
                    </View>
                  ) : (
                    <View style={{flexDirection: 'row', gap: 8, alignItems: 'center', marginTop: 2}}>
                      <CCText fontSize={FontSizes[11]} style={{color: '#6E7681'}}>Is this right?</CCText>
                      <RNTouchableOpacity onPress={() => sendZoneFeedback('correct')} style={styles.fbBtn}>
                        <Text style={{color: '#16A34A', fontWeight: '700', fontSize: 12}}>✓ Correct</Text>
                      </RNTouchableOpacity>
                      <RNTouchableOpacity onPress={() => setFbReasonOpen(true)} style={styles.fbBtn}>
                        <Text style={{color: '#DC2626', fontWeight: '700', fontSize: 12}}>✗ Incorrect</Text>
                      </RNTouchableOpacity>
                    </View>
                  )}
                </View>
              );
            })()}

            {/* Board */}
            {currentActivePuzzle?.startingFEN ? (
              <View style={styles.modalBoard}>
                <ChessboardWithArrow
                  position={activePuzzleFen}
                  handleNewFen={() => {}}
                  lastMoveFen={activePuzzleFen}
                  boardOrientation={currentActivePuzzle.orientation || 'white'}
                  disabled={true}
                />
              </View>
            ) : (
              <View style={[styles.modalBoard, {alignItems: 'center', justifyContent: 'center'}]}>
                <CCText fontSize={FontSizes[13]} style={{color: '#9AA0A6'}}>Board unavailable</CCText>
              </View>
            )}

            {/* Moves tried */}
            <View style={styles.modalMoves}>
              <CCText fontSize={FontSizes[12]} style={styles.modalMovesLabel}>Moves tried</CCText>
              {puzzleModalRow && (puzzleModalRow.wrong_moves?.length ?? 0) === 0 && !puzzleModalRow.correct_move ? (
                <CCText fontSize={FontSizes[12]} style={{color: '#9AA0A6'}}>No moves yet</CCText>
              ) : (
                <View style={{flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 4}}>
                  {(puzzleModalRow?.wrong_moves ?? []).map((m, i) => (
                    <Text key={i} style={[styles.moveChipWrong, {fontSize: 13}]}>✗ {m}</Text>
                  ))}
                  {puzzleModalRow?.correct_move && (
                    <Text style={[styles.moveChipCorrect, {fontSize: 13}]}>▶ {puzzleModalRow.correct_move}</Text>
                  )}
                  {(puzzleModalRow?.stars_earned ?? 0) > 0 && (
                    <Text style={[styles.moveChipStar, {fontSize: 13}]}>+{puzzleModalRow!.stars_earned}★ puzzle</Text>
                  )}
                  {(puzzleModalRow?.tournament_wins ?? 0) > 0 && (
                    <Text style={[styles.moveChipTourney, {fontSize: 13}]}>🏆 {puzzleModalRow!.tournament_wins}W</Text>
                  )}
                  {(puzzleModalRow?.session_stars ?? 0) > 0 && (
                    <Text style={[styles.moveChipSession, {fontSize: 13}]}>★{puzzleModalRow!.session_stars} total</Text>
                  )}
                </View>
              )}
              {puzzleModalRow?.solve_time && (
                <CCText fontSize={FontSizes[12]} style={{color: '#6E7681', marginTop: 6}}>
                  Solved in {puzzleModalRow.solve_time}s · {puzzleModalRow.attempts} attempt{puzzleModalRow.attempts !== 1 ? 's' : ''}
                </CCText>
              )}
            </View>
            </ScrollView>

            {/* Action row — pinned below the scroll so it's always reachable */}
            <View style={{flexDirection: 'row', justifyContent: 'center', gap: 8, marginVertical: vs(12)}}>
              <RNTouchableOpacity onPress={() => { refetchLive(); }} style={styles.modalRefreshBtn}>
                <Text style={{color: CCColors.Primary.Brown, fontWeight: '700'}}>↻ Refresh</Text>
              </RNTouchableOpacity>
              {puzzleModalRow && (
                <RNTouchableOpacity
                  onPress={() => { setHistoryModalUserId(puzzleModalRow.user_id); setPuzzleModalRow(null); }}
                  style={styles.modalRefreshBtn}>
                  <Text style={{color: CCColors.Primary.Brown, fontWeight: '700'}}>📈 History</Text>
                </RNTouchableOpacity>
              )}
            </View>
          </View>
        </View>
      </Modal>

      {/* Tier 2: Class brief modal — Layout C dashboard style */}
      <Modal
        visible={briefModalOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setBriefModalOpen(false)}>
        <View style={styles.briefBackdrop}>
          <View style={styles.briefCard}>
            {/* Header */}
            <View style={styles.briefHead}>
              <CCText fontSize={FontSizes[15]} style={styles.briefTitle}>Class Brief</CCText>
              <RNTouchableOpacity onPress={() => refetchBrief()} style={styles.briefHeadBtn}>
                <Text style={styles.briefHeadBtnText}>↻</Text>
              </RNTouchableOpacity>
              <RNTouchableOpacity onPress={() => setBriefModalOpen(false)} style={styles.briefHeadBtn}>
                <Text style={styles.briefHeadBtnText}>✕</Text>
              </RNTouchableOpacity>
            </View>

            {briefLoading && !briefData ? (
              <View style={styles.briefLoad}>
                <CCText fontSize={FontSizes[13]} style={{color: '#9AA0A6'}}>Loading…</CCText>
              </View>
            ) : briefData ? (
              <ScrollView style={{maxHeight: 480}} contentContainerStyle={{padding: vs(14), gap: 12}}>
                {/* KPI pill row */}
                <View style={styles.kpiRow}>
                  <View style={styles.kpiPill}>
                    <Text style={[styles.kpiNum, styles.tnum]}>{briefData.total_solves}</Text>
                    <Text style={styles.kpiLbl}>solves</Text>
                  </View>
                  <View style={styles.kpiPill}>
                    <Text style={[styles.kpiNum, styles.tnum]}>
                      {briefData.class_avg_solve_time != null ? briefData.class_avg_solve_time : '—'}
                      <Text style={styles.kpiUnit}>{briefData.class_avg_solve_time != null ? 's' : ''}</Text>
                    </Text>
                    <Text style={styles.kpiLbl}>avg time</Text>
                  </View>
                  <View style={styles.kpiPill}>
                    <Text style={[styles.kpiNum, styles.tnum]}>★{briefData.top_mover?.stars ?? 0}</Text>
                    <Text style={styles.kpiLbl}>top ★</Text>
                  </View>
                  <View style={[styles.kpiPill, briefData.at_risk.length > 0 && styles.kpiPillAlert]}>
                    <Text style={[styles.kpiNum, styles.tnum, briefData.at_risk.length > 0 && {color: '#D14343'}]}>
                      {briefData.at_risk.length}
                    </Text>
                    <Text style={[styles.kpiLbl, briefData.at_risk.length > 0 && {color: '#D14343'}]}>
                      at risk
                    </Text>
                  </View>
                  {briefData.homework && (
                    <RNTouchableOpacity
                      onPress={() => setHomeworkExpanded(v => !v)}
                      style={[
                        styles.kpiPill,
                        briefData.homework.total_students > 0 &&
                          briefData.homework.completed_count / briefData.homework.total_students < 0.5 &&
                          styles.kpiPillAlert,
                        homeworkExpanded && styles.kpiPillActive,
                      ]}>
                      <Text style={[styles.kpiNum, styles.tnum]}>
                        {briefData.homework.completed_count}/{briefData.homework.total_students}
                      </Text>
                      <Text style={styles.kpiLbl}>homework</Text>
                    </RNTouchableOpacity>
                  )}
                </View>

                {/* 2-col spotlight cards */}
                <View style={styles.spotlightRow}>
                  {/* TOP TODAY */}
                  <View style={[styles.spotCard, styles.spotCardGold]}>
                    <View style={styles.spotHead}>
                      <Text style={styles.spotIcon}>🏆</Text>
                      <Text style={styles.spotLabel}>TOP TODAY</Text>
                    </View>
                    {briefData.top_mover ? (
                      <>
                        <Text style={styles.spotName} numberOfLines={1}>{briefData.top_mover.name}</Text>
                        <View style={styles.spotStatRow}>
                          <Text style={[styles.spotStat, styles.tnum]}>★{briefData.top_mover.stars}</Text>
                        </View>
                      </>
                    ) : (
                      <Text style={styles.spotEmpty}>No top yet</Text>
                    )}
                  </View>

                  {/* TRICKIEST */}
                  <RNTouchableOpacity
                    activeOpacity={briefData.hardest_puzzle ? 0.7 : 1}
                    onPress={() => { if (briefData.hardest_puzzle) setTrickiestOpen(true); }}
                    style={[styles.spotCard, styles.spotCardCoral]}>
                    <View style={styles.spotHead}>
                      <Text style={styles.spotIcon}>🎯</Text>
                      <Text style={styles.spotLabel}>TRICKIEST</Text>
                      {briefData.hardest_puzzle && <Text style={styles.spotTapHint}>tap ›</Text>}
                    </View>
                    {briefData.hardest_puzzle ? (
                      <>
                        <Text style={styles.spotName} numberOfLines={1}>
                          {briefData.hardest_puzzle.order_index
                            ? `Puzzle #${briefData.hardest_puzzle.order_index}`
                            : `P${briefData.hardest_puzzle.puzzle_id}`}
                          {briefData.hardest_puzzle.puzzle_name ? ` · ${briefData.hardest_puzzle.puzzle_name}` : ''}
                        </Text>
                        {/* progress bar */}
                        <View style={styles.spotBar}>
                          <View
                            style={[
                              styles.spotBarFill,
                              {
                                width: `${briefData.hardest_puzzle.solve_pct}%` as any,
                                backgroundColor: briefData.hardest_puzzle.solve_pct >= 50 ? '#22A06B' : '#E17B21',
                              },
                            ]}
                          />
                        </View>
                        <View style={styles.spotStatRow}>
                          <Text style={[styles.spotStat, styles.tnum]}>
                            {briefData.hardest_puzzle.solve_pct}% solved
                          </Text>
                          <Text style={[styles.spotSub, styles.tnum]}>
                            {briefData.hardest_puzzle.avg_attempts} avg
                          </Text>
                        </View>
                        {briefData.hardest_puzzle.common_wrong && (
                          <Text style={styles.spotMissed} numberOfLines={1}>
                            missed: <Text style={{fontWeight: '800', color: '#D14343'}}>{briefData.hardest_puzzle.common_wrong}</Text>
                          </Text>
                        )}
                      </>
                    ) : (
                      <Text style={styles.spotEmpty}>No puzzles yet</Text>
                    )}
                  </RNTouchableOpacity>
                </View>

                {/* AT RISK row(s) */}
                {briefData.at_risk.length > 0 && (
                  <View style={styles.atRiskSection}>
                    <View style={styles.atRiskHead}>
                      <Text style={styles.atRiskHeadIcon}>⚠</Text>
                      <Text style={styles.atRiskHeadLabel}>NEEDS ATTENTION</Text>
                      <Text style={[styles.atRiskHeadCount, styles.tnum]}>{briefData.at_risk.length}</Text>
                    </View>
                    {briefData.at_risk.map(s => (
                      <RNTouchableOpacity
                        key={s.user_id}
                        onPress={() => { setBriefModalOpen(false); setHistoryModalUserId(s.user_id); }}
                        style={styles.atRiskRow}>
                        <View style={styles.atRiskAvatar}>
                          <Text style={styles.atRiskAvatarText}>
                            {s.name.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()}
                          </Text>
                        </View>
                        <Text style={styles.atRiskName} numberOfLines={1}>{s.name}</Text>
                        <Text style={styles.atRiskStars}>★0</Text>
                        <Text style={styles.atRiskCaret}>›</Text>
                      </RNTouchableOpacity>
                    ))}
                  </View>
                )}

                {/* HOMEWORK record — revealed when the homework pill is tapped */}
                {briefData.homework && homeworkExpanded && (
                  <View style={styles.atRiskSection}>
                    <View style={styles.atRiskHead}>
                      <Text style={styles.hwHeadIcon}>📚</Text>
                      <Text style={styles.hwHeadLabel}>
                        HOMEWORK · LAST {briefData.homework.per_student[0]?.recent_total ?? 4}
                      </Text>
                      <Text style={styles.hwHeadCount}>
                        {briefData.homework.completed_count}/{briefData.homework.total_students}
                      </Text>
                    </View>
                    {[...briefData.homework.per_student]
                      .sort((a, b) => Number(a.latest_completed) - Number(b.latest_completed))
                      .map(s => (
                        <RNTouchableOpacity
                          key={s.user_id}
                          onPress={() => { setBriefModalOpen(false); setHistoryModalUserId(s.user_id); }}
                          style={styles.hwRow}>
                          <Text style={[styles.hwStatus, {color: s.latest_completed ? '#22A06B' : '#D14343'}]}>
                            {s.latest_completed ? '✓' : '✗'}
                          </Text>
                          <Text style={styles.hwName} numberOfLines={1}>{s.name}</Text>
                          <View style={styles.hwDots}>
                            {s.history.map((h, i) => (
                              <Text key={i} style={{fontSize: 11, color: h.completed ? '#22A06B' : '#D9C9A3'}}>
                                {h.completed ? '●' : '○'}
                              </Text>
                            ))}
                          </View>
                          <Text style={styles.hwFrac}>{s.recent_completed}/{s.recent_total}</Text>
                          <Text style={styles.hwCaret}>›</Text>
                        </RNTouchableOpacity>
                      ))}
                    {briefData.homework.per_student.length === 0 && (
                      <Text style={styles.hwEmpty}>No students joined this class</Text>
                    )}
                  </View>
                )}

                {/* Empty state */}
                {briefData.total_solves === 0 && briefData.at_risk.length === 0 && !briefData.hardest_puzzle && (
                  <View style={styles.briefEmpty}>
                    <CCText fontSize={FontSizes[13]} style={{color: '#9AA0A6'}}>
                      No class activity yet
                    </CCText>
                  </View>
                )}
              </ScrollView>
            ) : (
              <View style={styles.briefLoad}>
                <CCText fontSize={FontSizes[13]} style={{color: '#9AA0A6'}}>No data</CCText>
              </View>
            )}
          </View>
        </View>
      </Modal>

      {/* Trickiest puzzle detail — opened by tapping the brief TRICKIEST card */}
      <Modal
        visible={trickiestOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setTrickiestOpen(false)}>
        <View style={styles.modalBackdrop}>
          <View style={styles.modalCard}>
            <View style={styles.modalHeader}>
              <View style={{flex: 1}}>
                <CCText fontSize={FontSizes[14]} style={{fontWeight: '700'}}>
                  {briefData?.hardest_puzzle?.order_index
                    ? `Puzzle #${briefData.hardest_puzzle.order_index}`
                    : `P${briefData?.hardest_puzzle?.puzzle_id ?? ''}`}
                  {briefData?.hardest_puzzle?.puzzle_name ? ` · ${briefData.hardest_puzzle.puzzle_name}` : ''}
                </CCText>
                <CCText fontSize={FontSizes[12]} style={{color: '#6E7681'}}>
                  Trickiest puzzle
                </CCText>
              </View>
              <RNTouchableOpacity onPress={() => setTrickiestOpen(false)} style={styles.modalCloseBtn}>
                <Text style={{fontSize: 18, color: '#6E7681'}}>✕</Text>
              </RNTouchableOpacity>
            </View>

            {briefData?.hardest_puzzle?.starting_fen ? (
              <View style={styles.modalBoard}>
                <ChessboardWithArrow
                  position={resolvePuzzleFen(briefData.hardest_puzzle.starting_fen, briefData.hardest_puzzle.move_list)}
                  handleNewFen={() => {}}
                  lastMoveFen={resolvePuzzleFen(briefData.hardest_puzzle.starting_fen, briefData.hardest_puzzle.move_list)}
                  boardOrientation={briefData.hardest_puzzle.orientation || 'white'}
                  disabled={true}
                />
              </View>
            ) : (
              <View style={[styles.modalBoard, {alignItems: 'center', justifyContent: 'center'}]}>
                <CCText fontSize={FontSizes[13]} style={{color: '#9AA0A6'}}>Board unavailable</CCText>
              </View>
            )}

            <View style={styles.modalMoves}>
              <View style={{flexDirection: 'row', flexWrap: 'wrap', gap: 6}}>
                <Text style={[styles.moveChipSession, {fontSize: 13}]}>
                  {briefData?.hardest_puzzle?.solve_pct}% solved
                </Text>
                <Text style={[styles.moveChipTourney, {fontSize: 13}]}>
                  {briefData?.hardest_puzzle?.avg_attempts} avg attempts
                </Text>
                {briefData?.hardest_puzzle?.common_wrong && (
                  <Text style={[styles.moveChipWrong, {fontSize: 13}]}>
                    most missed: {briefData.hardest_puzzle.common_wrong}
                  </Text>
                )}
                {(briefData?.hardest_puzzle?.move_list?.length ?? 0) > 0 && (
                  <Text style={[styles.moveChipCorrect, {fontSize: 13}]}>
                    ▶ {briefData!.hardest_puzzle!.move_list!.join(' ')}
                  </Text>
                )}
              </View>
            </View>
          </View>
        </View>
      </Modal>

      {/* Tier 3: Longitudinal student history modal */}
      <Modal
        visible={!!historyModalUserId}
        transparent
        animationType="fade"
        onRequestClose={() => setHistoryModalUserId(null)}>
        <View style={styles.modalBackdrop}>
          <View style={styles.modalCard}>
            <View style={styles.modalHeader}>
              <CCText fontSize={FontSizes[14]} style={{fontWeight: '700', flex: 1}}>
                📈 Student History
              </CCText>
              <RNTouchableOpacity onPress={() => refetchLong()} style={{padding: 6, marginRight: 4}}>
                <Text style={{color: CCColors.Primary.Brown, fontWeight: '700'}}>↻</Text>
              </RNTouchableOpacity>
              <RNTouchableOpacity onPress={() => setHistoryModalUserId(null)} style={styles.modalCloseBtn}>
                <Text style={{fontSize: 18, color: '#6E7681'}}>✕</Text>
              </RNTouchableOpacity>
            </View>
            <View style={styles.briefBody}>
              {longLoading && !longitudinalData ? (
                <CCText fontSize={FontSizes[12]} style={{color: '#6E7681'}}>Loading…</CCText>
              ) : longitudinalData ? (
                <>
                  <View style={styles.briefStatsRow}>
                    <View style={styles.briefStat}><Text style={styles.briefStatLabel}>Sessions</Text><Text style={styles.briefStatVal}>{longitudinalData.sessions.length}</Text></View>
                    <View style={styles.briefStat}><Text style={styles.briefStatLabel}>Total ★</Text><Text style={styles.briefStatVal}>{longitudinalData.total_stars_all_time}</Text></View>
                    <View style={styles.briefStat}><Text style={styles.briefStatLabel}>Wins</Text><Text style={styles.briefStatVal}>{longitudinalData.total_wins}</Text></View>
                    <View style={styles.briefStat}><Text style={styles.briefStatLabel}>Win rate</Text><Text style={styles.briefStatVal}>{longitudinalData.tournament_winrate}</Text></View>
                  </View>
                  {longitudinalData.trajectory.length > 0 && (
                    <View style={styles.briefSection}>
                      <Text style={styles.briefSectionLabel}>Star trajectory (oldest → newest)</Text>
                      <Sparkline values={longitudinalData.trajectory} />
                    </View>
                  )}
                  {longitudinalData.sessions.length > 0 && (
                    <View style={styles.briefSection}>
                      <Text style={styles.briefSectionLabel}>Per session</Text>
                      {longitudinalData.sessions.map((s, i) => (
                        <View key={i} style={styles.sessionRow}>
                          <Text style={styles.sessionDate}>{s.date || '—'}</Text>
                          <Text style={styles.sessionMetric}>★{s.stars}</Text>
                          <Text style={styles.sessionMetric}>✓{s.solves}</Text>
                          <Text style={styles.sessionMetric}>🏆{s.wins}</Text>
                        </View>
                      ))}
                    </View>
                  )}
                </>
              ) : (
                <CCText fontSize={FontSizes[12]} style={{color: '#9AA0A6'}}>No history yet</CCText>
              )}
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

// Simple SVG sparkline (uses View bar chart since RN has no SVG built-in)
const Sparkline: React.FC<{values: number[]}> = ({values}) => {
  if (values.length === 0) return null;
  const max = Math.max(...values, 1);
  return (
    <View style={{flexDirection: 'row', alignItems: 'flex-end', height: 40, gap: 4, paddingTop: 4}}>
      {values.map((v, i) => (
        <View key={i} style={{flex: 1, alignItems: 'center'}}>
          <View style={{
            width: '100%', maxWidth: 28,
            height: Math.max(2, (v / max) * 36),
            backgroundColor: CCColors.Primary.Brown, borderRadius: 3,
          }} />
          <Text style={{fontSize: 9, color: '#9AA0A6', marginTop: 2}}>{v}</Text>
        </View>
      ))}
    </View>
  );
};

// Column widths — must match header + data rows
const COL = {rank: 24, status: 80, stars: 40, action: 72};

const styles = StyleSheet.create({
  container: {flex: 1, padding: vs(12), backgroundColor: '#fff'},
  tabBar: {
    flexDirection: 'row',
    marginBottom: vs(10),
    borderRadius: 10,
    backgroundColor: '#F1F2F4',
    padding: 4,
    width: '100%',
    alignSelf: 'stretch',
  },
  tabBtn: {
    flexBasis: 0, flexGrow: 1, flexShrink: 1,
    paddingVertical: vs(10), paddingHorizontal: 8,
    borderRadius: 8, alignItems: 'center', justifyContent: 'center',
  },
  tabBtnActive: {
    backgroundColor: '#fff',
    shadowColor: '#000', shadowOpacity: 0.08, shadowRadius: 4,
    shadowOffset: {width: 0, height: 1}, elevation: 2,
  },
  tabBtnText: {color: '#6E7681', fontWeight: '600', textAlign: 'center'},
  tabBtnTextActive: {color: CCColors.Primary.Brown, fontWeight: '700'},
  subTitle: {color: '#6E7681', marginBottom: vs(4)},
  scrollView: {flex: 1},
  emptyState: {alignItems: 'center', paddingVertical: vs(32)},
  emptyDescription: {color: '#6E7681', textAlign: 'center'},

  // Table
  tableHeader: {
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: vs(8), paddingVertical: vs(6),
    borderBottomWidth: 1, borderBottomColor: '#E8EAED',
    backgroundColor: '#F8F9FA',
  },
  colHeader: {fontSize: 11, fontWeight: '700', color: '#9AA0A6', textTransform: 'uppercase'},
  tableRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: vs(8), paddingVertical: vs(9),
    borderBottomWidth: 1, borderBottomColor: '#F1F2F4',
  },
  tableRowAlt: {backgroundColor: '#FAFAFB'},
  tableRowCorrect: {borderLeftWidth: 3, borderLeftColor: '#22A06B'},
  colRank: {width: COL.rank, flexShrink: 0},
  colName: {flex: 1, marginHorizontal: 4, overflow: 'hidden'},
  colStatus: {width: COL.status, flexShrink: 0},
  colStars: {width: COL.stars, flexShrink: 0},
  colAction: {width: COL.action, flexShrink: 0},
  rankText: {fontSize: 12, fontWeight: '700', color: '#9AA0A6', textAlign: 'center'},
  liveName: {fontWeight: '600', flexShrink: 1},
  expandCaret: {color: '#9AA0A6', fontSize: 10},
  starBadge: {fontSize: 11, fontWeight: '700', color: '#B8780C', backgroundColor: '#FFF6E0', paddingHorizontal: 5, paddingVertical: 1, borderRadius: 6},
  badge: {paddingHorizontal: 7, paddingVertical: 3, borderRadius: 10},

  // Expandable detail row
  detailRow: {
    paddingHorizontal: vs(8), paddingVertical: vs(6),
    backgroundColor: '#F8F9FA',
    borderBottomWidth: 1, borderBottomColor: '#F1F2F4',
    gap: 6,
  },
  detailSection: {gap: 3},
  detailSectionLabel: {fontSize: 10, fontWeight: '700', color: '#9AA0A6', textTransform: 'uppercase'},
  chipRow: {flexDirection: 'row', flexWrap: 'wrap', gap: 4},
  badgeSubRow: {flexDirection: 'row', flexWrap: 'wrap', gap: 4, paddingHorizontal: vs(8), paddingBottom: vs(6), paddingTop: 0},
  inlineBadgeChip: {fontSize: 11, fontWeight: '700', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 8, borderWidth: 1},

  // Award picker
  awardOpenBtn: {backgroundColor: CCColors.Primary.Brown, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6, alignItems: 'center'},
  pickerContainer: {padding: vs(8), backgroundColor: '#F8F9FA', borderBottomWidth: 1, borderBottomColor: '#F1F2F4'},
  awardGrid: {flexDirection: 'row', flexWrap: 'wrap', gap: 6},
  awardChip: {flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 16, marginRight: 6, marginBottom: 6},
  awardChipIcon: {fontSize: 14, marginRight: 4},
  awardChipText: {color: '#fff', fontWeight: '700', fontSize: 12},
  confirmBox: {backgroundColor: '#FFF8E1', padding: 10, borderRadius: 8},
  confirmText: {marginBottom: 8},
  confirmBtns: {flexDirection: 'row', justifyContent: 'flex-end', gap: 8},
  btnNo: {paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6, backgroundColor: '#eee', marginRight: 8},
  btnYes: {paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6, backgroundColor: CCColors.Primary.Brown},

  // Awards feed
  feedRow: {flexDirection: 'row', alignItems: 'center', padding: vs(10), backgroundColor: '#FAFAFB', borderRadius: 8, marginBottom: 6},
  feedIcon: {width: 36, height: 36, alignItems: 'center', justifyContent: 'center', marginRight: 12},
  feedName: {fontWeight: '700'},
  feedLabel: {color: '#6E7681'},
  feedTime: {color: '#9AA0A6'},

  // Move chips
  moveChipCorrect: {fontSize: 11, fontWeight: '700', color: '#22A06B', backgroundColor: '#E5F6EE', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 8},
  moveChipWrong: {fontSize: 11, fontWeight: '700', color: '#D14343', backgroundColor: '#FDECEC', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 8},
  moveChipStar: {fontSize: 11, fontWeight: '700', color: '#B8780C', backgroundColor: '#FFF6E0', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 8},
  moveChipTourney: {fontSize: 11, fontWeight: '700', color: '#5B6BFA', backgroundColor: '#EDEFFE', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 8},
  moveChipSession: {fontSize: 11, fontWeight: '600', color: '#6E7681', backgroundColor: '#F1F2F4', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 8},

  // Puzzle detail modal
  modalBackdrop: {flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'center', alignItems: 'center', padding: 16},
  modalCard: {backgroundColor: '#fff', borderRadius: 14, width: '100%', maxWidth: 480, maxHeight: '90%', overflow: 'hidden'},
  modalHeader: {flexDirection: 'row', alignItems: 'center', padding: vs(14), borderBottomWidth: 1, borderBottomColor: '#F1F2F4'},
  modalCloseBtn: {padding: 6},
  modalBoard: {width: '100%', aspectRatio: 1, backgroundColor: '#FAF6EB'},
  modalMoves: {padding: vs(14), borderTopWidth: 1, borderTopColor: '#F1F2F4'},
  modalMovesLabel: {fontWeight: '700', color: '#4D3F37', marginBottom: 2},
  modalRefreshBtn: {alignSelf: 'center', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8, borderWidth: 1.5, borderColor: CCColors.Primary.Brown},
  fbBtn: {paddingHorizontal: 10, paddingVertical: 7, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(38,35,34,0.2)', backgroundColor: '#fff'},
  fbBtnPrimary: {paddingHorizontal: 14, paddingVertical: 7, borderRadius: 8, backgroundColor: CCColors.Primary.Brown},

  // Insights additions
  subTitleRow: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'},
  briefBtn: {paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6, borderWidth: 1, borderColor: CCColors.Primary.Brown + '55'},
  alertTicker: {flexDirection: 'row', flexWrap: 'wrap', gap: 4, paddingVertical: 4, marginBottom: 4},
  alertChip: {fontSize: 11, fontWeight: '700', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 8, borderWidth: 1},
  stuckDot: {color: '#D14343', fontSize: 10},
  briefBody: {padding: vs(14), gap: 10},
  briefSummary: {color: '#4D3F37', fontWeight: '600'},
  briefStatsRow: {flexDirection: 'row', justifyContent: 'space-between', gap: 6},
  briefStat: {flex: 1, alignItems: 'center', padding: 6, backgroundColor: '#FAF6EB', borderRadius: 6},
  briefStatLabel: {fontSize: 10, color: '#6E7681', textTransform: 'uppercase'},
  briefStatVal: {fontSize: 16, fontWeight: '800', color: '#4D3F37'},
  briefSection: {gap: 4},
  briefSectionLabel: {fontSize: 10, fontWeight: '700', color: '#9AA0A6', textTransform: 'uppercase'},
  briefSectionVal: {fontSize: 13, color: '#4D3F37'},
  atRiskChip: {fontSize: 12, fontWeight: '700', color: '#D14343', backgroundColor: '#FDECEC', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8, marginRight: 4},
  sessionRow: {flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4, borderBottomWidth: 1, borderBottomColor: '#F1F2F4'},
  sessionDate: {fontSize: 12, color: '#6E7681', flex: 2},
  sessionMetric: {fontSize: 12, fontWeight: '700', color: '#4D3F37', flex: 1, textAlign: 'right'},

  // ─── Class Brief — Layout C dashboard ────────────────────────────
  briefBackdrop: {flex: 1, backgroundColor: 'rgba(15,15,15,0.55)', alignItems: 'center', justifyContent: 'center', padding: 16},
  briefCard: {backgroundColor: '#FFFFFF', borderRadius: 16, width: '100%', maxWidth: 500, overflow: 'hidden', shadowColor: '#000', shadowOpacity: 0.2, shadowRadius: 20, shadowOffset: {width: 0, height: 6}},
  briefHead: {flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: '#F1F2F4'},
  briefTitle: {fontWeight: '800', color: CCColors.Primary.Brown, flex: 1, letterSpacing: 0.2},
  briefHeadBtn: {width: 32, height: 32, alignItems: 'center', justifyContent: 'center', borderRadius: 8, marginLeft: 4},
  briefHeadBtnText: {fontSize: 16, color: '#6E7681', fontWeight: '700'},
  briefLoad: {padding: 32, alignItems: 'center', justifyContent: 'center'},
  briefEmpty: {padding: 24, alignItems: 'center', justifyContent: 'center'},

  // KPI row
  kpiRow: {flexDirection: 'row', gap: 6},
  kpiPill: {flex: 1, paddingHorizontal: 8, paddingVertical: 10, borderRadius: 12, backgroundColor: '#FAF6EB', alignItems: 'center', minWidth: 0},
  kpiPillAlert: {backgroundColor: '#FDECEC'},
  kpiNum: {fontSize: 18, fontWeight: '800', color: CCColors.Primary.Brown, fontVariant: ['tabular-nums']},
  kpiUnit: {fontSize: 11, fontWeight: '700', color: '#9AA0A6'},
  kpiLbl: {fontSize: 9, fontWeight: '700', color: '#9AA0A6', textTransform: 'uppercase', letterSpacing: 0.6, marginTop: 2},
  tnum: {fontVariant: ['tabular-nums']},

  // Spotlight cards
  spotlightRow: {flexDirection: 'row', gap: 8},
  spotCard: {flex: 1, padding: 12, borderRadius: 12, backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: '#F1F2F4', borderLeftWidth: 3, minWidth: 0},
  spotCardGold: {borderLeftColor: '#FACF47', backgroundColor: '#FFFBED'},
  spotCardCoral: {borderLeftColor: '#E17B21', backgroundColor: '#FFF6EE'},
  spotHead: {flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: 6},
  spotIcon: {fontSize: 14},
  spotLabel: {fontSize: 10, fontWeight: '800', color: '#6E7681', textTransform: 'uppercase', letterSpacing: 0.8},
  spotTapHint: {fontSize: 10, fontWeight: '700', color: '#E17B21', marginLeft: 'auto'},
  spotName: {fontSize: 15, fontWeight: '800', color: CCColors.Primary.Brown, marginBottom: 4},
  spotStatRow: {flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 4},
  spotStat: {fontSize: 13, fontWeight: '700', color: CCColors.Primary.Brown},
  spotSub: {fontSize: 11, fontWeight: '600', color: '#9AA0A6'},
  spotEmpty: {fontSize: 12, color: '#9AA0A6', fontStyle: 'italic'},
  spotBar: {height: 6, borderRadius: 3, backgroundColor: '#F1F2F4', overflow: 'hidden', marginTop: 2, marginBottom: 4},
  spotBarFill: {height: '100%', borderRadius: 3},
  spotMissed: {fontSize: 11, fontWeight: '600', color: '#6E7681', marginTop: 6},

  // At-risk
  atRiskSection: {gap: 6},
  atRiskHead: {flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 2},
  atRiskHeadIcon: {fontSize: 12, color: '#D14343'},
  atRiskHeadLabel: {fontSize: 10, fontWeight: '800', color: '#D14343', textTransform: 'uppercase', letterSpacing: 0.8, flex: 1},
  atRiskHeadCount: {fontSize: 11, fontWeight: '800', color: '#D14343', backgroundColor: '#FDECEC', paddingHorizontal: 7, paddingVertical: 1, borderRadius: 9, overflow: 'hidden'},
  atRiskRow: {flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 10, paddingVertical: 10, borderRadius: 10, backgroundColor: '#FDECEC', borderWidth: 1, borderColor: '#F4C2C2'},
  atRiskAvatar: {width: 28, height: 28, borderRadius: 14, backgroundColor: '#FFFFFF', alignItems: 'center', justifyContent: 'center'},
  atRiskAvatarText: {fontSize: 10, fontWeight: '800', color: '#D14343'},
  atRiskName: {flex: 1, fontSize: 13, fontWeight: '700', color: CCColors.Primary.Brown},
  atRiskStars: {fontSize: 12, fontWeight: '800', color: '#9AA0A6'},
  atRiskCaret: {fontSize: 18, color: '#D14343', fontWeight: '700', marginLeft: 4},
  kpiPillActive: {borderWidth: 1.5, borderColor: CCColors.Primary.Brown},
  hwHeadIcon: {fontSize: 12},
  hwHeadLabel: {fontSize: 10, fontWeight: '800', color: CCColors.Primary.Brown, textTransform: 'uppercase', letterSpacing: 0.8, flex: 1},
  hwHeadCount: {fontSize: 11, fontWeight: '800', color: CCColors.Primary.Brown, backgroundColor: '#FAF6EB', paddingHorizontal: 7, paddingVertical: 1, borderRadius: 9, overflow: 'hidden'},
  hwRow: {flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 10, paddingVertical: 9, borderRadius: 10, backgroundColor: '#FAF6EB', borderWidth: 1, borderColor: '#ECE3CC'},
  hwStatus: {fontSize: 14, fontWeight: '900', width: 14, textAlign: 'center'},
  hwName: {flex: 1, fontSize: 13, fontWeight: '700', color: CCColors.Primary.Brown},
  hwDots: {flexDirection: 'row', gap: 3, alignItems: 'center'},
  hwFrac: {fontSize: 11, fontWeight: '800', color: '#6B6B6B', minWidth: 26, textAlign: 'right'},
  hwCaret: {fontSize: 18, color: '#9AA0A6', fontWeight: '700', marginLeft: 2},
  hwEmpty: {fontSize: 12, color: '#9AA0A6', paddingVertical: 6, paddingHorizontal: 10},
});

export default ClassroomActivityPanel;
